import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Layout, TabPanel, Tabs } from 'tea-component';
import { connect } from 'umi';
import ContentBody from '@op/components/ContentBody';
import useLanguage from '@op/hooks/useLanguage';
import useTabHistory from '@op/hooks/useTabHistory';
import {
  buriedBtnPoint,
  useInitBuriedPointInfo,
} from '@op/operationLog/buriedPoint';
import ReportGpt from './ReportGpt';
import { useAIInsightsReport } from '../CustomAISummary/hooks';
import CustomGpt from './CustomGpt';
import KeywordAnalysisEmbed from './KeywordAnalysisEmbed';
import UrlAnalysisEmbed from './UrlAnalysisEmbed';
import CommonSummaryEmbed from './CommonSummaryEmbed';
import usePageName from '@op/hooks/usePageName';
import CardCommon from '@op/components/CardCommon';
import { useAiReportNewTag } from '../hooks';
import { AiInsightReportPoint } from '@op/operationLog/pages';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
import { goStatsDetailAction } from '@op/pages/stats/Detail';
import { useFrontWebConfig } from '@op/hooks/useFrontWebConfig';
import { ClassSpecial } from '@op/constants/common';
import './index.scss';

const { Content } = Layout;

interface AIInsightsReportType {
  gameInfo: any;
  wholeGameInfo: any;
  userInfo: any;
  listMenu: any;
}
/**
 * 渲染分发（独立 memo 子组件）：
 *  - 新口径：按 category 分流（linked / common / keyword / keyword_topic / url）
 *  - 兼容旧数据：无 category 时按 type 分发（menu → ReportGpt，其余 → CustomGpt）
 * 三类下钻分析（keyword / topic / url）以 isPreviews 只读模式内嵌渲染。
 * 作为独立组件，props 变化时只重渲染当前实例；不依赖 scrolling 的分支可被 memo 挡住。
 */
const TabBody = React.memo((props: any) => {
  const {
    tab,
    gameInfo,
    wholeGameInfo,
    userInfo,
    contentRef,
    scrolling,
    formatMessage,
    requestData,
  } = props;
  if (!tab) return null;
  const { type: menuType } = tab;
  const { category, object_type: objectType } = tab.ext || {};

  // 新口径：keyword / topic → KeywordAnalysisEmbed
  if (category === 'keyword' || category === 'keyword_topic') {
    return (
      <KeywordAnalysisEmbed
        category={category}
        ext={tab?.ext}
        gameInfo={gameInfo}
        wholeGameInfo={wholeGameInfo}
        userInfo={userInfo}
      />
    );
  }

  // 新口径：url → UrlAnalysisEmbed
  if (category === 'url') {
    return (
      <UrlAnalysisEmbed
        ext={tab?.ext}
        gameInfo={gameInfo}
        wholeGameInfo={wholeGameInfo}
        userInfo={userInfo}
      />
    );
  }

  // common 自定义 summary（普通/订阅）→ CommonSummaryEmbed 复用 PreviewSummary 预览体
  // task_type = 'temporary' → PreviewBody（非订阅）；'recurring' → InsightPreviewBody（订阅）
  if (category === 'common' && objectType === 'collect') {
    return (
      <CommonSummaryEmbed
        ext={tab?.ext}
        gameInfo={gameInfo}
        wholeGameInfo={wholeGameInfo}
        contentRef={contentRef}
      />
    );
  }

  // linked 报告 / 兼容旧 menu type → ReportGpt
  if (category === 'linked' || menuType === 'menu') {
    return (
      <ReportGpt
        wholeGameInfo={wholeGameInfo}
        contentRef={contentRef}
        scrolling={scrolling}
        gameInfo={gameInfo}
        defaultParams={tab?.ext || {}}
        formatMessage={formatMessage}
        pageName={tab?.name}
      />
    );
  }

  // 兼容旧数据：无 category 时按 type 落 CustomGpt
  return (
    <CustomGpt
      wholeGameInfo={wholeGameInfo}
      contentRef={contentRef}
      scrolling={scrolling}
      gameInfo={gameInfo}
      task={tab?.ext}
      requestData={requestData}
      pageName={tab?.name}
    />
  );
});
TabBody.displayName = 'TabBody';

const AIInsightsReport = (props: AIInsightsReportType) => {
  const { gameInfo, wholeGameInfo, userInfo, listMenu } = props;

  useInitBuriedPointInfo({
    buriedPoint: AiInsightReportPoint,
  });

  const contentRef = useRef<HTMLElement>(null);
  const [scrolling, setScrolling] = useState(false);
  const { formatMessage, isEn } = useLanguage();
  const [changeTabLoading, setChangeTabLoading] = useState(false);
  // 滚动状态节流：滚动期间保持 true，停止 150ms 后置 false。
  // 用固定 handler（而非 render 内新建 debounce）保证节流真正生效，
  // 避免 scrolling 在 true/false 间抖动导致父组件频繁 re-render。
  const scrollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleContentScroll = useCallback(() => {
    setScrolling(true);
    if (scrollTimer.current) clearTimeout(scrollTimer.current);
    scrollTimer.current = setTimeout(() => {
      setScrolling(false);
    }, 150);
  }, []);
  const { tab: activeId, onActive } = useTabHistory<string>({
    removeKeys: ['report_start_time', 'report_end_time'],
  });

  const pageLoaded = useRef(false);
  const { menuId, sourceType } = usePageName({
    listMenu,
    name: 'AI Insights Report',
  });
  const AiInsightReportList = useFrontWebConfig({
    keys: 'ai_insights_report_list',
  });
  const { menuTab, repeatListData, loading, requestData } = useAIInsightsReport(
    {
      gameInfo,
      menuId,
      pageLoaded,
      sourceType,
    },
  );

  const {
    loading: newTagLoading,
    noNewTagsReport,
    setReportView,
  } = useAiReportNewTag({
    data: repeatListData?.list,
  });

  function JSONParse(str: string) {
    try {
      return JSON.parse(str);
    } catch (e) {
      return null;
    }
  }

  const tabs = useMemo(() => {
    const repeatList = (repeatListData?.list || []).map((item: any) => {
      const { id, name, name_cn } = item;
      const nameEn = name || name_cn;
      const nameCn = name_cn || name;
      let label = isEn ? nameEn : nameCn;

      if (!newTagLoading && !noNewTagsReport?.[id]) {
        label = (
          <div className="dbo-inlineflex">
            {label}
            <span className="dbo-new-tag">NEW</span>
          </div>
        );
      }

      return {
        id: `custom_${id}`,
        label,
        type: 'custom',
        taskId: id,
        ext: item,
        name,
      };
    });

    const defaultList = (menuTab || []).map((item: any) => {
      const { id, name_en, name_cn, ext_params, ...restItem } = item;
      const nameEn = name_en || name_cn;
      const nameCn = name_cn || name_en;
      const ext: any = JSONParse(ext_params);
      return {
        id: `menu_${id}`,
        label: (
          <div className="dbo-inlineflex">
            {/* <SvgIcon type="time" /> */}
            {isEn ? nameEn : nameCn}
          </div>
        ),
        type: 'menu',
        ext,
        name: nameEn,
        ...restItem,
      };
    });
    defaultList.sort((a, b) => a.sort - b.sort);

    const aiInsightReports = (AiInsightReportList || []).map((item: any) => {
      const { id, nameEn, nameCn, reportType, kind, type, ...resetData } = item;
      return {
        id: `${reportType}_${id}`,
        label: <div className="dbo-inlineflex">{isEn ? nameEn : nameCn}</div>,
        type: reportType,
        ext: { kind, type },
        name: nameEn,
        ...resetData,
      };
    });

    return [...aiInsightReports, ...defaultList, ...repeatList];
  }, [isEn, repeatListData, menuTab, noNewTagsReport, newTagLoading]);

  useEffect(() => {
    if (pageLoaded.current && tabs?.length > 0 && !loading) {
      if (!!!activeId) {
        onActive({ id: tabs?.[0]?.id });
      }
    }
  }, [activeId, loading, onActive, tabs]);

  // 权限控制
  const { Generate_Repeat_Report } = useGetSingePageSourceRule({
    keys: ['Generate_Repeat_Report'],
  });

  const emptyProps = useMemo(() => {
    let emptyDescText = `Once the report is created, the system will periodically update it automatically.\nPlease contact {email} if you need to create a periodic report.`;
    let emptyDescElement = null;

    if (!!!Generate_Repeat_Report) {
      emptyDescText =
        'Click the button below to create a new report, and it will automatically update based on your selected schedule.';
      emptyDescElement = (
        <>
          <Button
            type="primary"
            onClick={() => {
              goStatsDetailAction({
                path: `/opinion/AIAnalysisHub/CustomAISummary`,
                params: {},
              });
            }}
          >
            {formatMessage({ id: 'Generate Report' })}
          </Button>
        </>
      );
    }

    return {
      hasEmptyImg: false,
      emptyTitleText: 'No insight report has been created yet.',
      emptyDescText,
      emptyDescElement,
    };
  }, [Generate_Repeat_Report, formatMessage]);

  // 当前选中 tab 为 keyword / keyword_topic 时，其内嵌页（KeywordAnalysisEmbed）已自带
  // TimezoneTips 渲染报告来源提示，外层 ContentBody 不再重复渲染时区等系统提示；其余 tab 维持原样。
  const isActiveKeywordEmbed = useMemo(() => {
    const activeTab = tabs.find((tab) => tab.id === activeId);
    const { category: activeCategory } = activeTab?.ext || {};
    return (
      activeCategory === 'keyword' || activeCategory === 'keyword_topic'
    );
  }, [tabs, activeId]);
  /**
   * 渲染分发：tab body 渲染委托给独立 memo 子组件 TabBody（见文件顶部）。
   */
  return (
    <Content
      className="dbo-page dbo-ai-insights-report-wrapper"
      ref={contentRef}
      onScrollCapture={handleContentScroll}
    >
      <ContentBody
        noShowTimeZoneTips={isActiveKeywordEmbed}
        contentHeard={
          <Content.Header
            className={tabs?.length > 0 && !loading ? 'contentHead-normal' : ''}
            title={formatMessage({ id: 'AI Insight Report' })}
          >
            {tabs?.length > 0 && (
              <div className="header-tab">
                <Tabs
                  ceiling
                  tabs={tabs}
                  animated={false}
                  activeId={activeId!}
                  onActive={(value: any) => {
                    onActive(value);
                    if (
                      value.type === 'custom' &&
                      !noNewTagsReport?.[value.taskId]
                    ) {
                      setReportView(String(value.taskId));
                    }
                    buriedBtnPoint('ChangeReport', value.name);
                    setChangeTabLoading(true);
                    setTimeout(() => {
                      setChangeTabLoading(false);
                    }, 500);
                  }}
                />
              </div>
            )}
          </Content.Header>
        }
      >
        <CardCommon
          contentCard
          empty={tabs.length === 0}
          emptyHeight={600}
          loading={changeTabLoading || loading}
          loadingHeight={600}
          {...emptyProps}
        >
          <Tabs
            className={ClassSpecial.TAB_NONE}
            activeId={activeId}
            destroyInactiveTabPanel={false}
            animated={false}
          >
            {tabs.map((tab) => {
              // 仅 linked/menu（默认 CustomGpt）报告需响应滚动；keyword/url/common 内嵌预览不需要。
              // 不需要时 scrolling 传 undefined，使这些 TabBody 的 props 不变、被 memo 挡住，不随滚动重渲染。
              const { category } = tab.ext || {};
              const scrollSensitive =
                category !== 'keyword' &&
                category !== 'keyword_topic' &&
                category !== 'url' &&
                category !== 'common';
              return (
                <TabPanel key={tab.id} id={tab.id}>
                  <TabBody
                    tab={tab}
                    gameInfo={gameInfo}
                    wholeGameInfo={wholeGameInfo}
                    userInfo={userInfo}
                    contentRef={contentRef}
                    scrolling={scrollSensitive ? scrolling : undefined}
                    formatMessage={formatMessage}
                    requestData={requestData}
                  />
                </TabPanel>
              );
            })}
          </Tabs>
        </CardCommon>
      </ContentBody>
    </Content>
  );
};

export default connect((state: any) => {
  const { opinionCommon, home } = state;
  return {
    gameInfo: opinionCommon.gameInfo,
    wholeGameInfo: opinionCommon.wholeGameInfo,
    userInfo: home.userInfo,
    listMenu: opinionCommon.listMenu,
  };
})(AIInsightsReport);
